import React, { useMemo, useState } from 'react';

import type { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { AccountsRolesAutocompleteValue } from 'src/components';
import { AccountsRolesAutoComplete, getIsExistingUserOption, ListEmpty } from 'src/components';
import { GroupUserCell } from 'src/components/admin';
import { useAccessGuard } from 'src/components/auth';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { UserIcon } from 'src/components/icons/UserIcon';
import { Apollo } from 'src/gql/generated';
import { overflowMenuCSSId } from 'src/helpers';
import {
  createDataGridPremiumFooterlessOverrideStyles,
  useAddUsersToGroup,
  useGetGroupUsers,
} from 'src/hooks';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

export const groupAdminRowHeightPx = 52;
const useStyles = makeStyles()(() => ({
  emptyIcon: {
    height: 32,
    width: 32,
  },
  listContainer: {
    maxHeight: groupAdminRowHeightPx * 10,
    minHeight: groupAdminRowHeightPx * 10,
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover #${overflowMenuCSSId}`]: {
        visibility: 'visible',
      },
    },
  },
}));

export interface GroupUserRow {
  groupId: UUID;
  id: UUID;
  user: UserSubset;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GroupUserGridDef<CellValue = any, FormattedValue = any> = GridColDef<
  GroupUserRow,
  CellValue,
  FormattedValue
>;

interface GroupUsersListProps {
  groupId: UUID;
}

export function GroupUsersList({ groupId }: GroupUsersListProps): JSX.Element {
  const { classes } = useStyles();
  const { addUsersToGroup } = useAddUsersToGroup();
  const { groupUsers, loading } = useGetGroupUsers(groupId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'user' satisfies keyof GroupUserRow, sort: 'asc' },
  ]);
  const columnDefs: GroupUserGridDef[] = [
    {
      field: 'user',
      flex: 1,
      headerName: 'Name',
      renderCell: (params: GridRenderCellParams<GroupUserRow>) => <GroupUserCell {...params} />,
      sortable: false,
    },
  ];
  const rows = useMemo((): GroupUserRow[] => {
    return groupUsers
      .map(user => ({ groupId, id: user.id, user }))
      .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));
  }, [groupId, groupUsers]);
  const handleAddUserToGroup = useAccessGuard({
    callback: async (value: AccountsRolesAutocompleteValue) => {
      if (!getIsExistingUserOption(value)) return;

      await addUsersToGroup(groupId, {
        variables: { input: { id: groupId, userIds: [value.id] } },
      });
    },
    minRole: Apollo.OrgRole.Admin,
  });

  return (
    <>
      <AccountsRolesAutoComplete
        clearInputOnBlur
        clearOnBlur
        filterUsersPred={user => !groupUsers.some(u => u.id === user.id)}
        includeOptions="users"
        value={null}
        onChange={async (_e, value) => handleAddUserToGroup(value)}
      />
      <div className={classes.listContainer}>
        <DataGridPremium<GroupUserRow>
          disableColumnFilter
          disableColumnMenu
          disableColumnReorder
          disableColumnResize
          disableRowSelectionOnClick
          hideFooter
          className={classes.xGrid}
          columnHeaderHeight={groupAdminRowHeightPx}
          columns={columnDefs}
          loading={loading}
          rowHeight={groupAdminRowHeightPx}
          rows={rows}
          slotProps={{
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
          }}
          slots={{
            baseTooltip: GridAlineTooltip,
            noRowsOverlay: GroupUserListEmpty,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumFooterlessOverrideStyles}
          onSortModelChange={setSortModel}
        />
      </div>
    </>
  );
}

function GroupUserListEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<UserIcon className={classes.emptyIcon} fill="#9CA7AB" />}
      subtitle="Add users above"
      title="No users in the group"
    />
  );
}
